import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Lock, LogIn, Mail, Key, Loader2, Crown, Shield } from 'lucide-react';
import { Input } from '../../components/ui/input';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState({ email: '', password: '' });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          navigate('/admin', { replace: true });
          return;
        }
      } catch (error) {
        // Not authenticated
      }
      
      // Check if redirected from Hive login with auto-login flag
      const state = location.state;
      if (state?.autoLogin && state?.email && state?.password) {
        setCredentials({ email: state.email, password: state.password });
        // Clear the location state to prevent re-login on refresh
        window.history.replaceState({}, document.title);
        // Auto-submit the form
        performLogin(state.email, state.password);
        return;
      }
      
      setIsChecking(false);
    };
    
    checkAuth();
  }, [navigate, location.state]);

  const performLogin = async (email, password) => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/admin-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      if (response.ok) {
        const data = await response.json();
        // Store admin credentials for section pages
        localStorage.setItem('adminToken', 'admin-session-active');
        localStorage.setItem('adminUser', JSON.stringify(data.user));
        navigate('/admin', { replace: true, state: { user: data.user } });
      } else {
        const data = await response.json();
        setError(data.detail || 'Login failed');
        setIsChecking(false);
      }
    } catch (err) {
      setError('Connection error. Please try again.');
      setIsChecking(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    await performLogin(credentials.email, credentials.password);
  };

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center animate-pulse">
            <Crown className="w-6 h-6 text-black" />
          </div>
          <p className="text-white/50 text-sm">Verifying access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#050505]" data-testid="admin-login-page">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Image - Queen B Roses & Bees */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://customer-assets.emergentagent.com/job_525e34bb-6b75-419d-808a-8a9a7fc82752/artifacts/11wrjsef_IMG_0158.jpeg)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-transparent" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center gold-glow">
              <Crown className="w-7 h-7 text-black" />
            </div>
            <div>
              <h1 className="text-white font-black text-xl tracking-tight">QUEEN B</h1>
              <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em]">Logistics</p>
            </div>
          </Link>
          
          <div>
            <h2 className="text-5xl font-black text-white mb-4 tracking-tight leading-tight">
              COMMAND<br />
              <span className="text-[#D4AF37]">CENTER</span>
            </h2>
            <p className="text-white/60 text-lg max-w-md">
              Manage carriers, track operations, and grow your dispatch empire from one powerful dashboard.
            </p>
          </div>
          
          <div className="flex items-center gap-4 text-white/30 text-xs uppercase tracking-[0.2em]">
            <Shield className="w-4 h-4" />
            <span>Authorized Personnel Only</span>
          </div>
        </div>
      </div>
      
      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-3">
              <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center">
                <Crown className="w-7 h-7 text-black" />
              </div>
              <div className="text-left">
                <h1 className="text-white font-black text-xl tracking-tight">QUEEN B</h1>
                <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.3em]">Logistics</p>
              </div>
            </Link>
          </div>
          
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
              <Lock className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <h1 className="text-3xl font-black text-white mb-2 tracking-tight uppercase">
              Admin Portal
            </h1>
            <p className="text-white/40 text-sm">
              Sign in to access the Command Center
            </p>
          </div>

          <div className="glass p-8">
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-3">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                {error}
              </div>
            )}
            
            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="text-white/40 text-xs uppercase tracking-wider mb-2 block">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    type="email"
                    required
                    value={credentials.email}
                    onChange={(e) => setCredentials({...credentials, email: e.target.value})}
                    className="input-dark pl-12 h-14 w-full"
                    placeholder="admin@queenblogistics.com"
                    data-testid="admin-email"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-white/40 text-xs uppercase tracking-wider mb-2 block">Password</label>
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                  <Input
                    type="password"
                    required
                    value={credentials.password}
                    onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                    className="input-dark pl-12 h-14 w-full"
                    placeholder="Enter password"
                    data-testid="admin-password"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full flex items-center justify-center gap-3 h-14 gold-glow-hover"
                data-testid="admin-login-btn"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Access Command Center
                  </>
                )}
              </button>
            </form>
          </div>

          <p className="text-white/20 text-xs text-center mt-8 uppercase tracking-[0.2em]">
            Restricted Access • Queen B Logistics
          </p>

          {/* Link to Hive Login */}
          <div className="text-center mt-6">
            <Link 
              to="/hive/login"
              className="text-[#D4AF37] hover:text-[#F5D47B] text-sm transition-colors"
              data-testid="switch-to-hive-login"
            >
              Are you a carrier? Sign in to The Hive →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
